<template>
  <div>
    <template v-if="isLoadingOverlayShown || isShowPreLoader">
      <client-only>
        <v-lottie position="fixed" />
      </client-only>
    </template>
    <form @submit.prevent="" @keyup="cathEventCodes" autocomplete="on">
      <div class="display-flex flex-column align-items-start gap-10 w-100 relative z-index-10">
        <div class="w-100">
          <div class="general-input">
            <div
              :class="[
                `field field-text`,
                {
                  'field-invalid': errors.has('email'),
                  'field-filled': loginInfo.email.length,
                },
              ]"
            >
              <input
                id="email"
                type="text"
                v-model="loginInfo.email"
                name="email"
                data-testid="email-input"
                v-validate="'required|email'"
                data-vv-validate-on="blur"
                @focus="$validator.errors.remove('email')"
                :class="[
                  'input',
                  {
                    invalid: errors.has('email'),
                    empty: loginInfo.email.length <= 0,
                  },
                ]"
                autocomplete="on"
              />
              <label for="email" data-testid="email-label">
                {{ $t('placeholders.text.field.email') }}
              </label>
            </div>
            <div class="field-error" data-testid="email-field-error" v-if="errors.first('email')">
              <p>
                {{ errors.first('email') }}
              </p>
            </div>
          </div>
        </div>

        <div class="w-100">
          <div class="general-input">
            <div
              :class="[
                `field field-text`,
                {
                  'field-invalid': errors.has('password'),
                  'field-filled field-password': loginInfo.password.length,
                },
              ]"
            >
              <input
                id="password"
                :type="passwordFieldType ? 'password' : 'text'"
                v-model="loginInfo.password"
                name="password"
                data-testid="password-input"
                v-validate="'required|min:6'"
                data-vv-validate-on="blur"
                @focus="$validator.errors.remove('password')"
                :class="[
                  'input',
                  {
                    invalid: errors.has('password'),
                    empty: loginInfo.password.length <= 0,
                  },
                ]"
              />
              <label for="password" data-testid="password_label">
                {{ $t('placeholders.text.field.password') }}
              </label>
              <span
                v-if="!errors.has('password') || loginInfo.password.length > 0"
                :class="[
                  'cursor-pointer icon-password',
                  passwordFieldType ? 'airalo-icon-eye-slash' : 'airalo-icon-eye',
                  { 'tw-text-red-600': errors.has('password') },
                ]"
                @click="passwordFieldType = !passwordFieldType"
              />
            </div>
            <div class="field-error" data-testid="password-field-error" v-if="errors.first('password')">
              <p>
                {{ errors.first('password') }}
              </p>
            </div>
          </div>
        </div>

        <div class="w-100 display-flex justify-content-between align-items-start">
          <div class="display-flex justify-content-start align-items-center mt-10">
            <Toggle
              class="v-middle"
              data-testid="remember-me-toggle"
              :value="loginInfo.remember_me"
              v-on:change="loginInfo.remember_me = arguments[0]"
            />
            <span
              :class="[
                'typo-checkbox',
                {
                  'ml-10': $dir() === 'ltr',
                  'mr-10': $dir() === 'rtl',
                },
              ]"
              data-testid="remember-me-tittle"
              >{{ $t('components.login.remember') }}</span
            >
          </div>
          <div class="display-flex justify-content-start align-items-center">
            <nuxt-link
              :to="localePath({ name: 'verify-index-respass-1' })"
              class="typo-p color-text-primary text-decoration-underline"
              data-testid="forgot-password-link"
            >
              {{ $t('components.login.forgot-password-title') }}
            </nuxt-link>
          </div>
        </div>

        <div class="w-100 my-10">
          <button
            type="button"
            @click.prevent="validate"
            class="button button-secondary block btn-primary-hv"
            data-testid="login-button"
          >
            {{ $t('components.login.cta-button') }}
          </button>
        </div>

        <div class="display-flex justify-content-center align-items-center relative mt-10 w-100">
          <div class="centered-y left-0 w-100 h-1 color-bg-lighter z-index-10 line-width-100"></div>
          <p
            class="display-inline-flex justify-content-center align-items-center mb-0 relative z-index-20 px-20 typo-row color-text-secondary color-bg-body color-bg-white-sm"
          >
            {{ $t('components.login.seperator-text') }}
          </p>
        </div>

        <div class="w-100 mt-20">
          <SocialLogin @onClickSocialLink="onClickSocialLink" />
        </div>
      </div>
    </form>

    <client-only>
      <prompt-modal
        :modal-view="isAppleNamePromptShown"
        :overlay-view="false"
        type="double"
        :text="$t('components.social.login.title')"
        :button="{
          text: $t('global.cancel'),
          callbackName: 'closePromptModal',
          isClose: true,
        }"
        :button-second="{
          text: $t('global.continue'),
          callbackName: 'submitNameForApple',
          variant: 'prompt-simple-secondary',
        }"
        @onCloseModal="closePromptModal"
        @onClickButton="submitNameForApple"
      >
        <template>
          <div class="rename-modal-body mt-10">
            <p class="esim-rename-subtitle typo-p color-text-secondary text-center mb-0">
              {{ $t('components.social.login.subtitle') }}
            </p>

            <div class="display-flex flex-column gap-10 mt-20">
              <div class="general-input">
                <div
                  :class="[
                    `field field-text`,
                    {
                      'field-invalid': errors.has('appleFirstName'),
                      'field-filled': appleFirstName.length,
                    },
                  ]"
                >
                  <input
                    id="appleFirstName"
                    type="text"
                    v-model="appleFirstName"
                    name="appleFirstName"
                    v-validate="'required|min:2|max:255'"
                    data-vv-validate-on="blur"
                    @focus="$validator.errors.remove('appleFirstName')"
                    :class="[
                      'input',
                      {
                        invalid: errors.has('appleFirstName'),
                        empty: appleFirstName.length <= 0,
                      },
                    ]"
                    :data-vv-as="$t('placeholders.text.field.first-name')"
                  />
                  <label for="appleFirstName" data-tid="appleFirstName_label">
                    {{ $t('placeholders.text.field.first-name') }}
                  </label>
                </div>
                <div class="field-error" v-if="errors.first('appleFirstName')">
                  <p>
                    {{ errors.first('appleFirstName') }}
                  </p>
                </div>
              </div>

              <div class="general-input">
                <div
                  :class="[
                    `field field-text`,
                    {
                      'field-filled': appleLastName.length,
                    },
                  ]"
                >
                  <input
                    id="apple_last_name"
                    v-model="appleLastName"
                    type="text"
                    name="apple_last_name"
                    :class="[
                      'input',
                      {
                        empty: appleLastName.length <= 0,
                      },
                    ]"
                    :data-vv-as="$t('placeholders.text.field.last-name')"
                  />
                  <label for="appleFirstName" data-tid="appleFirstName_label">
                    {{ $t('placeholders.text.field.last-name') }}
                  </label>
                </div>
                <div class="field-error" v-if="errors.first('appleFirstName')">
                  <p>
                    {{ errors.first('appleFirstName') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </template>
      </prompt-modal>
    </client-only>
  </div>
</template>

<script>
  import debounce from 'lodash.debounce'
  import cryptoRandomString from 'crypto-random-string'
  import URLJoin from 'url-join'
  import JSCookies from 'js-cookie'
  import { addYears } from 'date-fns'

  export default {
    components: {
      Toggle: () => import('~/components/input/toggle.vue'),
      SocialLogin: () => import('~/components/social/social-login.vue'),
      PromptModal: () => import('~/components/modals/prompt-simple.vue'),
    },

    middleware: 'is-auth',

    data() {
      return {
        passwordFieldType: true,

        loginInfo: {
          email: '',
          password: '',
          remember_me: true,
        },

        appleFirstName: '',
        appleLastName: '',
        isAppleNamePromptShown: false,

        appleSignInCredentials: {
          token: null,
          first_name: '',
          last_name: '',
        },

        isLoadingOverlayShown: false,
        isShowPreLoader: false,
      }
    },

    head() {
      return {
        title: this.$t('seo.auth.login.title'),
        meta: [
          {
            hid: 'description',
            name: 'description',
            content: this.$t('seo.auth.login.description'),
          },
          {
            hid: 'keywords',
            name: 'keywords',
            content: this.$t('seo.auth.login.keywords'),
          },
          {
            property: 'og:title',
            content: this.$t('seo.auth.login.title'),
          },
          {
            property: 'og:description',
            content: this.$t('seo.auth.login.description'),
          },
        ],
      }
    },

    methods: {
      cathEventCodes: debounce(function (event) {
        if (event.key === 'Enter' || event.keyCode === 13) {
          this.validate()
        }
      }, 200),

      validate() {
        this.$validator.validateAll().then((result) => {
          if (result) {
            this.login()
          }
        })
      },

      async login() {
        this.isLoadingOverlayShown = true

        try {
          await this.$auth.loginWith('local', {
            data: {
              email: this.loginInfo.email,
              password: this.loginInfo.password,
            },
          })

          await this.$store.dispatch('userSettings/fetch_settings')

          this.$utils.sendEvent('loginEvent', { method: 'form', status: 'success' })
          this.$utils.sendEvent('typeClearEvent')

          this.isLoadingOverlayShown = false

          if (this.loginInfo.remember_me) {
            localStorage.setItem('remember-email', this.loginInfo.email)
          }

          this.$router.push(this.localePath('/auth/callback'))
        } catch (error) {
          console.error({ error })

          this.$utils.sendEvent('loginEvent', { method: 'form', status: 'fail' })

          this.isLoadingOverlayShown = false
          await this.errorHandler(error)
        }
      },

      onClickSocialLink(payload) {
        this.loginWith(payload)
      },

      async loginWith(provider) {
        this.$cookiz.set('auth.provider', provider, {
          httpOnly: false,
          expires: addYears(new Date(), 1),
          path: '/',
        })
        this.isShowPreLoader = true

        const selectedLangFromCookie = JSCookies.get('Airalo.locale')

        JSCookies.set('Airalo.last_locale', selectedLangFromCookie, {
          expires: 1,
        })

        if (provider === 'apple') {
          this.getAppleCredentials().finally(() => {
            this.isShowPreLoader = false
          })
        } else {
          try {
            await this.$auth.loginWith(provider)
            await this.$store.dispatch('userSettings/fetch_settings')

            this.isShowPreLoader = false
          } catch (error) {
            this.isShowPreLoader = false
          }
        }
      },

      closePromptModal() {
        this.isAppleNamePromptShown = false

        setTimeout(() => {
          this.isShowPreLoader = false
          this.isLoadingOverlayShown = false
        }, 100)
      },

      openPrompModal() {
        this.isAppleNamePromptShown = true
      },

      async getAppleCredentials() {
        try {
          const data = await AppleID.auth.signIn()

          if (data?.user?.name?.firstName) {
            this.appleSignInCredentials.first_name = data.user.name.firstName
            this.appleFirstName = data.user.name.firstName
          }

          if (data?.user?.name?.lastName) {
            this.appleSignInCredentials.last_name = data.user.name.lastName
            this.appleLastName = data.user.name.lastName
          }

          this.appleSignInCredentials.token = data.authorization.id_token
          this.signInWithApple()
        } catch (error) {}
      },

      async signInWithApple() {
        let authResponse = null

        this.isLoadingOverlayShown = true

        const socialLoginPayload = {
          token: this.appleSignInCredentials.token,
          first_name: this.appleSignInCredentials.first_name,
          last_name: this.appleSignInCredentials.last_name,
        }

        try {
          authResponse = await this.$axios.post('/v4/auth/social/apple', socialLoginPayload)
          // const socialLoginData = await this.$store.dispatch('post_social_login', {
          //     authStrategy: 'apple',
          //     socialLoginPayload,
          //   })
        } catch (err) {
          if (err.response.data.code === 'auth.social.missing') {
            if (err.response.data?.user?.token) {
              this.appleSignInCredentials.token = err.response.data.user.token
            }

            this.openPrompModal()
            return
          }
        }

        if (!authResponse) {
          return
        }

        const auth = this.$auth

        try {
          await auth.setUserToken(authResponse.data.token)
          await auth.setStrategy('local')
          await auth.fetchUser()

          await this.$store.dispatch('userSettings/fetch_settings')

          if (authResponse.is_first_login) {
            this.$utils.sendEvent('signupEvent', { method: 'apple' })
          } else {
            this.$utils.sendEvent('loginEvent', { method: 'apple', status: 'success' })
          }

          const redirectURL = localStorage.getItem('redirect-url')

          if (redirectURL) {
            localStorage.removeItem('redirect-url')
            this.$router.push(this.localePath(redirectURL))
          } else {
            this.$router.push(this.localePath('/'))
          }
        } catch (err) {
          this.isShowPreLoader = false
          this.isLoadingOverlayShown = false
        }
      },

      submitNameForApple() {
        if (!this.appleFirstName) {
          this.$bvToast.toast(this.$t('messages.social.missing.not-filled-error'), {
            title: this.$t('global.error.title'),
            variant: 'danger',
          })

          return
        }

        this.closePromptModal()

        this.appleSignInCredentials.first_name = this.appleFirstName
        this.appleSignInCredentials.last_name = this.appleLastName

        this.isShowPreLoader = true

        this.signInWithApple()
      },

      errorHandler(error) {
        if (typeof error === 'object' && error.response && error.response.status === 400) {
          const { data } = error.response

          if (data.code === 'auth.verify.required') {
            this.$store.commit('set_auth_dialogs_email', this.loginInfo.email)

            window.localStorage.setItem('set_auth_dialogs_email', this.loginInfo.email)
            this.$router.push(
              this.localePath({
                name: 'verify-index-activation',
                query: {
                  email: this.loginInfo.email,
                },
              })
            )
          } else {
            this.$bvToast.toast(data.message, {
              title: this.$t('global.error.title'),
              variant: 'danger',
              solid: true,
            })
          }
        } else if (typeof error === 'object' && error.response && error.response.status === 429) {
          const { data } = error.response
          this.$bvToast.toast(data.message, {
            title: this.$t('global.error.title'),
            variant: 'danger',
            solid: true,
          })
        }
      },
    },

    mounted() {
      window.AppleID.auth.init({
        clientId: process.env.APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: URLJoin(process.env.APP_URL, 'auth/callback'),
        state: cryptoRandomString({ length: 256 }),
        usePopup: true,
      })

      const rememberedEmail = localStorage?.getItem('remember-email') || ''

      if (rememberedEmail) {
        this.loginInfo.email = rememberedEmail
      }
    },
  }
</script>
